/**
 * Sincronización: primero lo que haya en el dispositivo (al instante), después el libro en segundo plano.
 * Solo se descarga si el fichero ha cambiado; el Archivo, solo si cambian su revisión o su huella,
 * o cada 30 días como red de seguridad.
 */
import { parseWorkbook, parseArchive, checkWorkbook, OPTIONAL_RANGES, type Workbook, type HealthReport, type ArchivedMovement } from '@pf/workbook';
import type { CachedWorkbook, LocalCache, WorkbookSource } from './ports.js';

export interface Loaded {
  wb: Workbook;
  archive: ArchivedMovement[];
  health: HealthReport;
  title: string;
  syncedAt: string;
  fromCache: boolean;
}

const ARCHIVE_MAX_AGE_DAYS = 30;

export function fromCached(c: CachedWorkbook): Loaded {
  const wb = parseWorkbook(c.ranges);
  return { wb, archive: parseArchive(c.archive), health: checkWorkbook(wb, c.formulas), title: c.title, syncedAt: c.syncedAt, fromCache: true };
}

export async function loadCached(source: WorkbookSource, cache: LocalCache): Promise<Loaded | null> {
  const c = await cache.load(source.id);
  return c ? fromCached(c) : null;
}

/** @return los datos nuevos, o null si el libro no ha cambiado desde la última vez. */
export async function sync(source: WorkbookSource, cache: LocalCache, force = false): Promise<Loaded | null> {
  const cached = await cache.load(source.id);
  const modifiedTime = await source.modifiedTime();
  const now = new Date().toISOString();
  // Una copia hecha por una versión anterior de la app puede no tener los rangos nuevos (p. ej. Notas): se relee
  const complete = !!cached && Object.keys(OPTIONAL_RANGES).every(k => k in cached.ranges);
  if (!force && cached && complete && cached.modifiedTime === modifiedTime) {
    await cache.save({ ...cached, syncedAt: now });
    return null;
  }
  const [data, title] = await Promise.all([source.readData(), source.title()]);
  const wb = parseWorkbook(data.ranges);
  const archiveKey = `${wb.control.archiveRevision}|${wb.control.archiveFingerprint}`;
  const stale = !cached || cached.archiveKey !== archiveKey || force
    || Date.now() - Date.parse(cached.archiveFetchedAt) > ARCHIVE_MAX_AGE_DAYS * 86400000;
  const archive = stale ? await source.readArchive() : cached!.archive;
  const value: CachedWorkbook = { sourceId: source.id, title, modifiedTime, ...data, archive, archiveKey,
    archiveFetchedAt: stale ? now : cached!.archiveFetchedAt, syncedAt: now };
  await cache.save(value);
  return { ...fromCached(value), fromCache: false };
}
